import React from 'react';

const AdminListCard = ({ borderAccent = 'border-gray-200', children, actions }) => (
  <article
    className={`bg-white rounded-xl border shadow-sm hover:shadow-md transition-all ${borderAccent}`}
  >
    <div className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">{children}</div>
        {actions && (
          <div className="flex gap-2 shrink-0 flex-wrap justify-end">{actions}</div>
        )}
      </div>
    </div>
  </article>
);

export const AdminMeta = ({ children }) => (
  <p className="text-gray-400 text-xs mt-3">{children}</p>
);

export const AdminBadge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-600',
    success: 'bg-green-50 text-green-700 border border-green-200/60',
    warning: 'bg-amber-50 text-amber-700 border border-amber-200/60',
    danger: 'bg-red-50 text-designer-primary border border-red-200/60',
    info: 'bg-blue-50 text-blue-700 border border-blue-200/60',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${variants[variant] || variants.default}`}
    >
      {children}
    </span>
  );
};

export const AdminActionButton = ({
  onClick,
  disabled,
  variant = 'default',
  icon,
  children,
  href,
  title,
}) => {
  const variants = {
    default: 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200/60',
    success: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200/60',
    info: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200/60',
    danger: 'text-gray-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200/60',
  };

  const className = `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 ${variants[variant] || variants.default}`;

  if (href) {
    return (
      <a href={href} className={className} title={title}>
        {icon}
        {children}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
      title={title}
    >
      {icon}
      {children}
    </button>
  );
};

export const AdminSectionHeader = ({ id, icon: Icon, title, action }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-9 h-9 rounded-xl bg-red-50 text-designer-primary flex items-center justify-center shrink-0 border border-red-100/60">
      <Icon />
    </div>
    <h2 id={id} className="font-bold text-designer-dark text-lg">{title}</h2>
    {action && <div className="ml-auto">{action}</div>}
  </div>
);

export const AdminStat = ({ icon: Icon, label, value, sub, tile }) => (
  <div className="bg-white rounded-xl border border-gray-200/80 p-4 sm:p-5 flex items-center gap-4 shadow-sm">
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tile}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-designer-dark tabular-nums leading-none">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-gray-500 mt-1.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5 truncate">{sub}</p>}
    </div>
  </div>
);

export default AdminListCard;
